import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import {
  projectBoardApi,
  useGetProjectQuery,
  useGetBoardWorkflowTasksQuery,
  useGetTaskStatusesQuery,
  useGetTimelineQuery,
  useGetAllPermissionsQuery,
  useGetBoardCriticalTaskQuery,
  useCreateTimelineRowMutation,
  usePatchTimelineRowMutation,
} from 'app/services/projectBoardApi';
import { ahuBoardApi } from 'app/services/ahuBoardApi';
import { useBoardCurrentUser } from '../../hooks/useProjectBoardData';
import type { ITimelineRow, IWorkflowTask } from 'models';

/**
 * WORKFLOW MANAGER POPUP (shared `/static/mvc/workflow_popup/view/
 * workflow_popup.js` + workflow_main.tmpl / workflow_item.tmpl), inventory
 * §3.11 / parity 99–118. Opened from the header `#workflow_popup_button`
 * AFTER a fresh project fetch (§1.9 `fn['bind.view.workflow_popup']`).
 *
 * Data: CMS workflow tasks (cms hardcoded 1), master task statuses, the
 * critical task (GET …/workflow/critical_tasks/; {} or fetch error ⇒ none),
 * allPermissions (single cached call per session — parity 116) and the
 * project timeline — SKIPPED entirely (reads AND writes) when the role has
 * no `project_board_tabs` entry for 'timeline' (parity 115).
 *
 * Checkbox semantics (parity 104–105): non-archive — Complete force-checks
 * Start, any Start click unchecks Complete (restart semantics); archive
 * tasks (cancelled/lost/closed) — checking Start auto-checks Complete,
 * unchecking Complete unchecks Start; while ANY archive task is checked all
 * other rows lock (mutex `non-clickable archive-mutex-locked`), released on
 * uncheck (and on archived projects a release also re-enables the
 * non-archive rows — the dev-delta `_updateArchiveTasksState(fromUserAction)`
 * behavior, replicated).
 *
 * Done (parity 109–114): derives per-task {status_id, actual dates, by ids}
 * from the 4-state checkbox matrix (preserve existing dates/authors, stamp
 * today/current-user for new ones), POSTs missing rows, PATCHes only rows
 * that actually differ (loose != compare), runs the critical-task save
 * strictly AFTER all non-critical saves (the legacy's stated intent — its
 * `Promise.all([nonCriticalTaskPromises])` wraps the ARRAY and resolves
 * immediately, and the critical CREATE path never resolves its promise; we
 * implement the documented checklist behavior #111 instead of those two
 * bugs), then applies the navigation rules; whenever those leave us on the
 * page a 500ms timer refreshes the status chip + timeline (the `Timeline`
 * and `AhuProjectTimeline` tags — the project board and the AHU board read
 * the timeline through different API slices) and fires the `workflow:done`
 * CustomEvent (AHU list seam — parity 113). Legacy ran that refresh only
 * when the critical task was untouched, which left the chip stale for the
 * two critical-task cases that navigate nowhere; we refresh in those too.
 * The popup closes IMMEDIATELY on Done while saves continue async.
 *
 * Esc/Cancel close without saving (global popup keys click `#cancel`).
 *
 * [NEG] honored: WorkflowPopupItemView's dead `change select`/changeStatus
 * handler + `Models.UpdateProjectTask` (parity 117) and the `keyAction`
 * alert handler (parity 118) are not rebuilt. The `checbox-view` class typo
 * is kept verbatim (CSS keys off it).
 */

const ARCHIVE_TASK_VALUES = ['cancelled', 'lost', 'closed'];

const pad = (n: number, width: number): string => String(n).padStart(width, '0');

type Checks = Record<number, { start: boolean; complete: boolean }>;

interface WorkflowPopupProps {
  projectId: number;
  onClose: () => void;
}

const WorkflowPopup = ({ projectId, onClose }: WorkflowPopupProps) => {
  const dispatch = useAppDispatch();

  const { data: project } = useGetProjectQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const { data: user } = useBoardCurrentUser();
  const { data: tasks } = useGetBoardWorkflowTasksQuery();
  const { data: statuses } = useGetTaskStatusesQuery();
  const { data: allPerms } = useGetAllPermissionsQuery();
  const criticalQ = useGetBoardCriticalTaskQuery();

  const perms = allPerms?.[0];
  // No 'timeline' tab row for the role ⇒ timelineCollection = null (saves skipped).
  const hasTimeline = !!perms?.permissions?.project_board_tabs?.find(
    (el) => el.tab === 'timeline'
  );
  const { data: timeline } = useGetTimelineQuery(projectId, {
    skip: !perms || !hasTimeline || !Number.isFinite(projectId),
  });

  const [createRow] = useCreateTimelineRowMutation();
  const [patchRow] = usePatchTimelineRowMutation();

  // CriticalTaskModel: error → 0; success with {} → no task_id (never matches).
  const criticalSettled = criticalQ.isSuccess || criticalQ.isError;
  const criticalTaskId = criticalQ.isError ? 0 : criticalQ.data?.task_id ?? 0;

  const ready = !!(
    project &&
    user &&
    tasks &&
    statuses &&
    perms &&
    criticalSettled &&
    (!hasTimeline || timeline)
  );

  // ---- initial per-task status (tasks_status_arranged; default 1) ----
  const initialStatusByTask = useMemo(() => {
    const map: Record<number, number> = {};
    (tasks ?? []).forEach((t) => {
      const row = timeline?.find(
        (r) => r.project_id === projectId && r.task_id === t.id
      );
      map[t.id] = row?.status_id ?? 1;
    });
    return map;
  }, [tasks, timeline, projectId]);

  // ---- checkbox state (2 ⇒ Start; 3 ⇒ Start+Complete — parity 101) ----
  const [checks, setChecks] = useState<Checks | null>(null);
  const [headsUp, setHeadsUp] = useState(false);
  const [archivedReleased, setArchivedReleased] = useState(false);

  useEffect(() => {
    if (ready && checks === null) {
      const init: Checks = {};
      tasks!.forEach((t) => {
        const s = initialStatusByTask[t.id] ?? 1;
        init[t.id] = { start: s === 2 || s === 3, complete: s === 3 };
      });
      setChecks(init);
    }
  }, [ready, checks, tasks, initialStatusByTask]);

  // ---- per-task Start/Complete permissions (parity 102) ----
  const taskPerms = useMemo(() => {
    const rows = perms?.permissions?.workflow_tasks ?? [];
    const map: Record<number, { start: boolean; complete: boolean }> = {};
    (tasks ?? []).forEach((t) => {
      const entry = rows.find((p) => p.workflow_task_id === t.id);
      let start = !!entry?.start_task;
      let complete = !!entry?.complete_task;
      // No start permission + status 1 ⇒ complete also locked.
      if (!start && (initialStatusByTask[t.id] ?? 1) === 1) complete = false;
      map[t.id] = { start, complete };
    });
    return map;
  }, [perms, tasks, initialStatusByTask]);

  if (!ready || !checks) return null;

  const statusesById: Record<number, string> = {};
  statuses!.forEach((s) => {
    statusesById[s.id] = s.name;
  });

  // Legacy reads the raw model attr: a falsy `editable` locks (no `!== false`).
  const editable = !!project!.editable;
  const isArchived = project!.project_state === 'archived';
  const criticalLocked =
    !!project!.is_critical_task_started && user!.access_type === 'project';
  const fullyLocked = !editable && !isArchived;
  const doneHidden = criticalLocked || fullyLocked;

  // ---- archive mutex: the checked archive task's value, or null ----
  let mutexValue: string | null = null;
  tasks!.forEach((t) => {
    if (ARCHIVE_TASK_VALUES.indexOf(t.value ?? '') !== -1) {
      const c = checks[t.id];
      if (c && (c.start || c.complete)) mutexValue = t.value ?? null;
    }
  });

  const isArchiveTask = (t: IWorkflowTask) =>
    ARCHIVE_TASK_VALUES.indexOf(t.value ?? '') !== -1;

  /** Label lock per render()'s gating + the archive mutex pass. */
  const labelClass = (t: IWorkflowTask, which: 'start' | 'complete'): string => {
    const isArch = isArchiveTask(t);
    // mutex: every row other than the checked archive task locks
    const mutexLocked = mutexValue !== null && t.value !== mutexValue;
    let base: boolean;
    if (criticalLocked || fullyLocked) {
      base = true;
    } else if (!editable && isArchived) {
      // archived: archive rows force-unlocked; others locked until released
      base = isArch ? false : !archivedReleased;
    } else {
      base = which === 'start' ? !taskPerms[t.id]?.start : !taskPerms[t.id]?.complete;
    }
    if (mutexLocked) {
      return base ? ' non-clickable' : ' non-clickable archive-mutex-locked';
    }
    return base ? ' non-clickable' : '';
  };

  /** changeTask side effects: critical heads-up + archive release. */
  const afterChange = (t: IWorkflowTask, next: Checks) => {
    if (t.id === criticalTaskId) {
      setHeadsUp(!!next[t.id]?.start);
    }
    let nextMutex: string | null = null;
    tasks!.forEach((task) => {
      if (isArchiveTask(task)) {
        const c = next[task.id];
        if (c && (c.start || c.complete)) nextMutex = task.value ?? null;
      }
    });
    if (nextMutex === null && isArchived) setArchivedReleased(true);
  };

  const clickStart = (t: IWorkflowTask) => {
    setChecks((prev) => {
      if (!prev) return prev;
      const cur = prev[t.id] ?? { start: false, complete: false };
      const newStart = !cur.start;
      // archive + checking → also check Complete; otherwise any Start click
      // unchecks Complete (restart semantics).
      const next: Checks = {
        ...prev,
        [t.id]: { start: newStart, complete: newStart && isArchiveTask(t) },
      };
      afterChange(t, next);
      return next;
    });
  };

  const clickComplete = (t: IWorkflowTask) => {
    setChecks((prev) => {
      if (!prev) return prev;
      const cur = prev[t.id] ?? { start: false, complete: false };
      const newComplete = !cur.complete;
      // archive + unchecking → uncheck Start; otherwise Start force-checked.
      const newStart = isArchiveTask(t) && !newComplete ? false : true;
      const next: Checks = {
        ...prev,
        [t.id]: { start: newStart, complete: newComplete },
      };
      afterChange(t, next);
      return next;
    });
  };

  const statusLabel = (t: IWorkflowTask): string => {
    const c = checks[t.id] ?? { start: false, complete: false };
    const id = c.complete ? 3 : c.start ? 2 : 1;
    return statusesById[id] ?? '';
  };

  // ---- donePopup ----
  const donePopup = () => {
    const date = new Date();
    const dateFormatted = [
      pad(date.getFullYear(), 4),
      pad(date.getMonth() + 1, 2),
      pad(date.getDate(), 2),
    ].join('-');
    const userId = user!.id;
    const projectSnapshot = project!;

    let criticalChanged = false;
    const nonCriticalJobs: Array<() => Promise<unknown>> = [];
    // At most one entry — the critical task's save, run AFTER the others.
    const criticalJobs: Array<() => Promise<unknown>> = [];

    tasks!.forEach((t) => {
      const c = checks[t.id] ?? { start: false, complete: false };
      const row = hasTimeline
        ? timeline?.find((r) => r.project_id === projectId && r.task_id === t.id)
        : undefined;
      const prevStart = row?.actual_start_date ?? null;
      const prevEnd = row?.actual_end_date ?? null;
      const prevStartBy = (row?.started_by_id as number | null | undefined) ?? null;
      const prevEndBy = (row?.completion_by_id as number | null | undefined) ?? null;

      const setObj: Partial<ITimelineRow> = {};
      if (!c.start && !c.complete) {
        setObj.status_id = 1;
        setObj.actual_start_date = null;
        setObj.actual_end_date = null;
        setObj.started_by_id = null;
        setObj.completion_by_id = null;
      } else if (c.start && !c.complete) {
        setObj.status_id = 2;
        setObj.actual_start_date = prevStart || dateFormatted;
        setObj.actual_end_date = null;
        setObj.started_by_id = prevStartBy || userId;
        setObj.completion_by_id = null;
      } else {
        // 1/1 and 0/1 both map to Complete (status 3).
        setObj.status_id = 3;
        setObj.actual_start_date = prevStart || dateFormatted;
        setObj.actual_end_date = prevEnd || dateFormatted;
        setObj.started_by_id = prevStartBy || userId;
        setObj.completion_by_id = prevEndBy || userId;
      }

      if (!row) {
        if (!hasTimeline) return; // timelineCollection null ⇒ saves skipped
        setObj.task_id = t.id;
        setObj.project_id = projectId;
        const job = () => createRow({ projectId, body: setObj }).unwrap();
        if (t.id === criticalTaskId) {
          criticalChanged = true;
          criticalJobs.push(job);
        } else {
          nonCriticalJobs.push(job);
        }
      } else {
        // Save only models that actually changed (loose != — null≈undefined).
        const changed = Object.keys(setObj).some(
          // eslint-disable-next-line eqeqeq
          (k) => (setObj as Record<string, unknown>)[k] != (row as Record<string, unknown>)[k]
        );
        if (!changed) return;
        const job = () =>
          patchRow({ projectId, id: row.id as number, body: setObj }).unwrap();
        if (t.id === criticalTaskId) {
          criticalChanged = true;
          criticalJobs.push(job);
        } else {
          nonCriticalJobs.push(job);
        }
      }
    });

    (async () => {
      // Critical-task ordering (parity 111): non-critical saves first.
      await Promise.all(nonCriticalJobs.map((job) => job().catch(() => undefined)));
      for (const job of criticalJobs) {
        // eslint-disable-next-line no-await-in-loop
        await job().catch(() => undefined);
      }

      let navigating = false;
      if (criticalChanged) {
        if (userId === projectSnapshot.user_id) {
          if (projectSnapshot.by_project_user) {
            // Project-user owner loses editing rights → full reload.
            window.location.reload();
            navigating = true;
          }
          // Network-user owner → stays on the page.
        } else if (projectSnapshot.by_project_user) {
          // Non-owner on a by-project project → it disappears for them.
          window.location.replace('/dashboard/');
          navigating = true;
        }
        // Other-network-user project → stays on the page.
      }

      if (!navigating) {
        setTimeout(() => {
          // #checkbox_status_changed change ⇒ status chip + timeline refetch
          // (shared Timeline tag), then the dispatcher workflow:done event.
          dispatch(
            projectBoardApi.util.invalidateTags([{ type: 'Timeline', id: projectId }])
          );
          // Same refresh for the AHU board's own copy of the status chip, which
          // reads the timeline through ahuBoardApi. No-op from the project board.
          dispatch(
            ahuBoardApi.util.invalidateTags([
              { type: 'AhuProjectTimeline', id: projectId },
            ])
          );
          window.dispatchEvent(new CustomEvent('workflow:done'));
        }, 500);
      }
    })();

    // Popup closes immediately; saves continue async (parity 114).
    onClose();
  };

  return (
    <div
      id="workflow_popup"
      className={`popup-wrapper workflow-popup active${
        criticalLocked ? ' critical-task-passed' : ''
      }`}
    >
      <div className="popup-content">
        <h2 className="popup-heading">Workflow Manager</h2>
        <ul className="clearfix workflow-content-heading">
          <li className="left">
            <h3>Task</h3>
          </li>
          <li className="right">
            <h3>Status</h3>
          </li>
        </ul>
        <div className="popup-content-part">
          <ul
            id="workflow_tasks_list"
            className={`list-rows input-label-group${headsUp ? ' extra-height' : ''}`}
          >
            {tasks!.map((t) => {
              const c = checks[t.id] ?? { start: false, complete: false };
              const isCriticalRow = t.id === criticalTaskId;
              const showNote = isCriticalRow && headsUp;
              return (
                <li
                  key={t.id}
                  className={`workflow-unit${showNote ? ' critical-task' : ''}`}
                  data-task={t.id}
                  data-task-value={t.value}
                >
                  <label className={`start-checkbox${labelClass(t, 'start')}`}>
                    <span className="checkbox-label">Start</span>
                    <input
                      className="hidden"
                      type="checkbox"
                      name="start"
                      checked={c.start}
                      onChange={() => clickStart(t)}
                    />
                    <span className="checbox-view" />
                    <span className="label" data-task={t.id}>
                      <i className={`icon-${t.id}`} /> {t.name}
                    </span>
                  </label>
                  <label className={`complete-checkbox${labelClass(t, 'complete')}`}>
                    <span className="checkbox-label">Complete</span>
                    <input
                      className="hidden"
                      type="checkbox"
                      name="complete"
                      checked={c.complete}
                      onChange={() => clickComplete(t)}
                    />
                    <span className="checbox-view" />
                  </label>
                  <span className="status">{statusLabel(t)}</span>
                  {showNote && (
                    <span className="heads-up-note">
                      <h5>Heads Up!</h5>
                      If you Start this task, you will lose all editing rights, which
                      will be transferred to the manufacturer’s Network users.
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
        <div className="popup-control-buttons">
          <button
            id="done"
            className="btn right highlighted-btn"
            style={doneHidden ? { display: 'none' } : undefined}
            onClick={donePopup}
          >
            Done
          </button>
          <button id="cancel" className="btn right not-highlighted-btn" onClick={onClose}>
            Cancel
          </button>
        </div>
        <div className="popup-note">Permission needed to Start or Complete a Task.</div>
        <div className={`critical-task-note${criticalLocked ? '' : ' hidden'}`}>
          The Critical Task has been started. You only have viewing rights to the project.
        </div>
      </div>
    </div>
  );
};

export default WorkflowPopup;
